import React from 'react'
import { useNavigate, useParams } from 'react-router'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm } from '@divvy-web/skylab.react-hook-form'

import { useGetCreditApplicationForReviewSuspenseQuery } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import {
  hasEmptyValue,
  hasMissingRequiredField,
  toSentenceCase,
} from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const BusinessAddressReviewSection = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { control } = useForm()
  const [getClassName] = useNameFormatter('BusinessAddressReviewSection')
  const { track } = useWebTracking()

  const { data } = useGetCreditApplicationForReviewSuspenseQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const { physicalAddress, mailingAddress, mailingPhysicalAddressesEqual } = data?.creditApplication?.businessInfo || {}

  const hasMissingInfo =
    !!data &&
    hasMissingRequiredField([
      ...(!mailingPhysicalAddressesEqual
        ? [mailingAddress?.street, mailingAddress?.city, mailingAddress?.postalCode, mailingAddress?.state]
        : []),
      physicalAddress?.street,
      physicalAddress?.city,
      physicalAddress?.postalCode,
      physicalAddress?.state,
    ])

  const handleOnEditClick = () => {
    track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Business Address',
      navigated_to_page: `${PATHNAME_CONSTS.BUSINESS_ADDRESS_PATH}`,
    })
    navigate(`../${PATHNAME_CONSTS.BUSINESS_ADDRESS_PATH}`)
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Business address'
            id='sputnik.BusinessAddressSection__RiFqwI'
          />
        }
        onEditClick={handleOnEditClick}
      >
        <span className={styles[getClassName('street')]}>
          <FormTextInput
            readOnly
            className='fs-mask'
            control={control}
            isRequired={hasEmptyValue(physicalAddress?.street)}
            label={
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='Business physical address'
                  id='sputnik.BusinessInfoReviewSection__4IvIJZ'
                />
              </span>
            }
            name='street'
            value={
              physicalAddress?.street
                ? `${physicalAddress?.street || ''} ${physicalAddress?.streetAdditional || ''}`
                : '—'
            }
          />
        </span>
        <div className={styles[getClassName('city-state')]}>
          <FormTextInput
            readOnly
            className='fs-mask'
            control={control}
            isRequired={hasEmptyValue(physicalAddress?.city)}
            label={
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='City'
                  id='sputnik.BusinessInfoReviewSection__TE4fIS'
                />
              </span>
            }
            name='city'
            value={physicalAddress?.city ? toSentenceCase(physicalAddress?.city) : '—'}
          />
          <FormTextInput
            readOnly
            className='fs-mask'
            control={control}
            isRequired={hasEmptyValue(physicalAddress?.state)}
            label={
              <span className='fs-unmask'>
                <FormattedMessage
                  defaultMessage='State'
                  id='sputnik.BusinessInfoReviewSection__ku+mDU'
                />
              </span>
            }
            name='state'
            value={physicalAddress?.state || '—'}
          />
        </div>
        <FormTextInput
          readOnly
          className='fs-mask'
          control={control}
          isRequired={hasEmptyValue(physicalAddress?.postalCode)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='ZIP code'
                id='sputnik.BusinessInfoReviewSection__iZL8VQ'
              />
            </span>
          }
          name='postalCode'
          value={physicalAddress?.postalCode || '—'}
        />
        {!mailingPhysicalAddressesEqual && (
          <>
            <span className={styles[getClassName('street')]}>
              <FormTextInput
                readOnly
                className='fs-mask'
                control={control}
                isRequired={hasEmptyValue(mailingAddress?.street)}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='Business mailing address'
                      id='sputnik.BusinessInfoReviewSection__NOd4Sq'
                    />
                  </span>
                }
                name='streetMailing'
                value={
                  mailingAddress?.street
                    ? `${mailingAddress?.street || ''} ${mailingAddress?.streetAdditional || ''}`
                    : '—'
                }
              />
            </span>
            <div className={styles[getClassName('city-state')]}>
              <FormTextInput
                readOnly
                className='fs-mask'
                control={control}
                isRequired={hasEmptyValue(mailingAddress?.city)}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='City'
                      id='sputnik.BusinessInfoReviewSection__TE4fIS'
                    />
                  </span>
                }
                name='cityMailing'
                value={mailingAddress?.city ? toSentenceCase(mailingAddress?.city) : '—'}
              />
              <FormTextInput
                readOnly
                className='fs-mask'
                control={control}
                isRequired={hasEmptyValue(mailingAddress?.state)}
                label={
                  <span className='fs-unmask'>
                    <FormattedMessage
                      defaultMessage='State'
                      id='sputnik.BusinessInfoReviewSection__ku+mDU'
                    />
                  </span>
                }
                name='stateMailing'
                value={mailingAddress?.state || '—'}
              />
            </div>
            <FormTextInput
              readOnly
              className='fs-mask'
              control={control}
              isRequired={hasEmptyValue(mailingAddress?.postalCode)}
              label={
                <span className='fs-unmask'>
                  <FormattedMessage
                    defaultMessage='ZIP code'
                    id='sputnik.BusinessInfoReviewSection__iZL8VQ'
                  />
                </span>
              }
              name='postalCodeMailing'
              value={mailingAddress?.postalCode || '—'}
            />
          </>
        )}
      </SectionCollapsibleList>
    </div>
  )
}

export default BusinessAddressReviewSection
